import { useRef, useState } from 'react';
import { Link } from 'react-router';
import { useAuth } from 'react-oidc-context';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  clearGraph,
  clearGraphs,
  graphClearJobStatus,
  createGraph,
  deleteGraph,
  deleteSource,
  sourceDeleteJobStatus,
  downloadGraph,
  downloadRules,
  listGitSources,
  listSources,
  loadFromGit,
  preflightRules,
  rdfContentType,
  startLoadJob,
  apiFailure,
  type ApiResult,
  type BarePrefixClaim,
  type ExportFormat,
  type GitSource,
  type LoadKind,
  type LoadMode,
  type LoadResponse,
  type RulePreflight,
  type SourceCount,
} from '@/lib/api';
import { describeApiError } from '@/lib/apiErrors';
import { ConfirmGraphReplaceModal } from '@/components/ConfirmGraphReplaceModal';
import { CLEAR_ALL_PHRASE, ConfirmGraphOpModal, type GraphOp } from '@/components/ConfirmGraphOpModal';
import { ALL_GRAPHS, GraphSelector, kindForBare, newGraphIri } from '@/components/GraphSelector';
import { fieldClass } from '@/components/ui/field';
import { clearJobLine } from '@/lib/clearJobs';
import { isUnionScope } from '@/lib/useGraphScope';
import { VdbBadge } from '@/components/VdbBadge';
import { LoadBanBanner } from '@/components/LoadBanBanner';
import { useLoadGate } from '@/lib/useLoadGate';
import { deferredDetail, useVdbStatus, type VdbStatus } from '@/lib/useVdb';
import { formatDuration } from '@/lib/time';
import { SpreadsheetImportPanel } from '@/components/SpreadsheetImportPanel';
import { RdfSpreadsheetImportPanel } from '@/components/RdfSpreadsheetImportPanel';
import { FolderImportPanel } from '@/components/FolderImportPanel';
import { ExtractPanel } from '@/components/ExtractPanel';
import { GremlinImportPanel } from '@/components/GremlinImportPanel';
import { LpgImportPanel } from '@/components/LpgImportPanel';
import { NamespaceRegistryPanel } from '@/components/NamespaceRegistryPanel';
import { TroubleshootPanel } from '@/pages/TroubleshootPanel';
import { invalidateAfterDataWrite } from '@/lib/invalidate';
import { useAuditLog, type AuditEntry } from '@/lib/useAdmin';
import { useLoadJobs } from '@/lib/loadJobs';
import { fetchSnapshotContents, restoreSnapshot } from '@/lib/api';
import { SnapshotModal } from '@/components/SnapshotModal';
import { snapshotHoldsLine } from '@/lib/snapshot';
import { useActiveDatabase, useCurrentDatabase, useDatabases } from '@/lib/useDatabases';
import { DatabaseSwitch } from '@/components/DatabaseSwitch';
import type { MapContext } from '@/components/FolderAnalysisReport';
import { graphDisplayLabel, useGraphs } from '@/lib/useGraphs';
import { canExportStore, canManageSettings, canImportData, isQros, useMe } from '@/lib/useMe';
import { usePropertyGraphImportEnabled } from '@/lib/usePages';
import { cn } from '@/lib/utils';
import { Message, messageClass } from '@/components/ui/Message';
import { Button, buttonVariants } from '@/components/ui/Button';

type PageTab = 'load' | 'graphs' | 'namespaces' | 'troubleshoot' | 'log' | 'backup';


/** Keep only the rows inside the operator's read scope. A union (or empty)
 *  scope keeps everything. */
function inScope<T>(rows: T[], graphs: string[], graphOf: (row: T) => string | null): T[] {
  if (graphs.length === 0 || isUnionScope(graphs)) return rows;
  return rows.filter((r) => {
    const g = graphOf(r);
    // A row with no graph of its own (an instance-wide operation in the log)
    // is never hidden by a graph filter — it belongs to no graph.
    return g === null || graphs.includes(g);
  });
}
type SourceTab = 'direct' | 'git';

const KIND_DESCRIPTIONS: Record<LoadKind, string> = {
  ontology: 'Schema: classes, properties, subclass hierarchy, domain/range.',
  vocabulary: 'Concepts and taxonomies — preferred label, broader, narrower.',
  data: 'Instance data (A-box) — actual facts about entities.',
  catalog: 'Catalog instance data — products, series, and issues browsed via the Catalog page.',
};

type State =
  | { kind: 'idle' }
  | { kind: 'uploading' }
  // A direct upload the server accepted; the load now runs in the background
  // and the completion notice reports it, so the user can leave this page.
  | { kind: 'accepted'; filename: string; graph: string }
  | { kind: 'done'; result: ApiResult<LoadResponse> }
  // The file declares the prefix `:` for a namespace other than QROS's, which
  // `:` always names (ruling 4.19): nothing was loaded, and the person picks.
  | { kind: 'bare_prefix'; claim: BarePrefixClaim; reason: string }
  | { kind: 'bare_prefix_cancelled'; namespace: string };

type LoadTab = 'graph' | 'spreadsheet' | 'folder' | 'extract' | 'gremlin' | 'lpg';

/** Everything on this page belongs to the database in use, so a switch starts
 *  every panel afresh: a folder plan, a mapping half drawn, a load's outcome
 *  and a review are the previous database's and never carry over. The tabs
 *  the user is on are kept, so the switch shows the same tab for the new one. */
export function DatabaseManagementPage() {
  const { active } = useActiveDatabase();
  const [pageTab, setPageTab] = useState<PageTab>('load');
  const [topTab, setTopTab] = useState<LoadTab>('spreadsheet');
  return (
    <DatabaseManagementBody
      key={active ?? ''}
      pageTab={pageTab}
      setPageTab={setPageTab}
      topTab={topTab}
      setTopTab={setTopTab}
    />
  );
}

function DatabaseManagementBody({
  pageTab,
  setPageTab,
  topTab,
  setTopTab,
}: {
  pageTab: PageTab;
  setPageTab: (tab: PageTab) => void;
  topTab: LoadTab;
  setTopTab: (tab: LoadTab) => void;
}) {
  const auth = useAuth();
  const me = useMe();
  const qros = isQros(me.data);
  // The whole-database backup is an Admin's and an Owner's, as whole-store export is (9.10).
  const canBackup = qros && canExportStore(me.data);
  const lpgEnabled = usePropertyGraphImportEnabled().data ?? true;
  const queryClient = useQueryClient();
  const { addLoadJob } = useLoadJobs();
  const token = auth.user?.id_token;
  // The database every control on this page writes to and reads, stated under
  // the heading on every tab, as the desktop's Database Management stated its
  // active profile. A load, a clear, a restore all land here.
  const { database: currentDatabase, count: databaseCount } = useCurrentDatabase();
  // While a load holds the gate, uploads are banned (one write at a time — the
  // VDB-collision ban). Navigation and reads stay open; only upload controls lock.
  const uploadsLocked = !!useLoadGate();
  // Read scope for the operator views (graph table, downloads, transaction
  // log). Cold-starts on the union so nothing is hidden on first visit.
  // The graph table and the transaction log always show every graph; the
  // load target is picked on the Load tab, beside the write (UAT 2026-08-29:
  // a scope picker in the header did not belong there).
  const scopeGraphs = [ALL_GRAPHS];
  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // The selector emits bare IRI form (`urn:tres:ontology` etc.). A managed
  // graph maps to a LoadKind via `kindForBare`; a custom graph (allowCreate)
  // has no kind and is targeted by URI on upload.
  const [graphBare, setGraphBare] = useState<string | null>(null);
  const kind: LoadKind = (kindForBare(graphBare ?? '') ?? 'ontology') as LoadKind;
  const [mode, setMode] = useState<LoadMode>('append');
  const [state, setState] = useState<State>({ kind: 'idle' });
  // A sheet handed over from the folder plan's Map action. Keyed remount so a
  // second Map starts the wizard fresh on the new file.
  const [mapTarget, setMapTarget] = useState<{ file: File; sheet?: string; context?: MapContext; seq: number } | null>(
    null,
  );
  const [pendingRuleLoss, setPendingRuleLoss] = useState<RulePreflight | null>(null);
  // Which load is waiting on the replace gate. Replace drops the target graph
  // before writing, so it is never a one-click action.
  const [pendingReplace, setPendingReplace] = useState<'upload' | 'git' | null>(null);
  const [sourceTab, setSourceTab] = useState<SourceTab>('direct');
  const [gitSourceId, setGitSourceId] = useState<string>('');
  const [gitPath, setGitPath] = useState<string>('');

  // The graph a load actually writes to — both tabs now send the selected
  // graph by URI, so the label IS the target.
  const targetGraphLabel = graphBare?.trim() ?? '';

  // Operator-configured git sources. Always fetched (cheap, cached by
  // react-query); we hide the Git tab entirely when zero are
  // configured.
  const sourcesQuery = useQuery<GitSource[], Error>({
    queryKey: ['git-sources'],
    enabled: !!token && canImportData(me.data),
    queryFn: async () => {
      if (!token) throw new Error('no token');
      const r = await listGitSources(token);
      if (!r.ok) throw new Error(r.message);
      return r.data.sources;
    },
  });
  const sources = sourcesQuery.data ?? [];
  const gitEnabled = sources.length > 0;

  if (me.isLoading) {
    return <Centered>Loading…</Centered>;
  }
  if (!canImportData(me.data)) {
    return (
      <Centered>
        <div className="font-medium">Not authorized</div>
        <div className="mt-1 text-muted-foreground">
          Your role does not include the <code>ImportData</code> action.
        </div>
      </Centered>
    );
  }

  const invalidateAfterLoad = (result: ApiResult<LoadResponse>) => {
    if (result.ok) {
      invalidateAfterDataWrite(queryClient);
    }
  };

  const handleLoadFromGit = async () => {
    if (!token || !gitSourceId || !gitPath.trim()) return;
    if (mode === 'replace') {
      setPendingReplace('git');
      return;
    }
    await performLoadFromGit();
  };

  const performLoadFromGit = async () => {
    if (!token || !gitSourceId || !gitPath.trim() || !graphBare) return;
    setPendingReplace(null);
    setState({ kind: 'uploading' });
    let result: ApiResult<LoadResponse>;
    try {
      result = await loadFromGit(
        { source_id: gitSourceId, path: gitPath.trim(), graph: graphBare, mode },
        token,
      );
    } catch (e) {
      result = apiFailure(0, 'load failed', e instanceof Error ? e.message : String(e));
    }
    setState({ kind: 'done', result });
    invalidateAfterLoad(result);
  };

  /**
   * Rule-retraction gate. Loading a `.tqs` whose `@rules "set" { … }` block
   * lists FEWER rules than the store deletes the remainder — a user restoring
   * a partial file loses the rest of the set silently. Ask the server what the
   * load would retract (it writes nothing) and stop for confirmation when the
   * answer isn't "nothing". A preflight that itself fails never blocks the
   * load; the load response still names every retraction.
   */
  const checkRuleRetractions = async (f: File): Promise<RulePreflight | null> => {
    if (!qros || !token || !f.name.toLowerCase().endsWith('.tqs')) return null;
    let text: string;
    try {
      text = await f.text();
    } catch {
      return null;
    }
    const result = await preflightRules(text, token);
    if (!result.ok || result.data.total_would_retract === 0) return null;
    return result.data;
  };

  const handleUpload = async () => {
    if (!token || !file) return;
    if (mode === 'replace') {
      setPendingReplace('upload');
      return;
    }
    await checkRulesThenUpload();
  };

  const checkRulesThenUpload = async () => {
    if (!token || !file) return;
    setPendingReplace(null);
    const retractions = await checkRuleRetractions(file);
    if (retractions) {
      setPendingRuleLoss(retractions);
      return;
    }
    await performUpload();
  };

  const performUpload = async (convertBarePrefix = false) => {
    if (!token || !file) return;
    setPendingRuleLoss(null);
    const graph = graphBare?.trim() ?? '';

    setState({ kind: 'uploading' });
    const contentType = rdfContentType(file.name);
    const filename = file.name;
    const accepted = await startLoadJob(
      file,
      contentType,
      graph,
      token,
      mode,
      filename,
      convertBarePrefix,
    );
    if (!accepted.ok && accepted.barePrefix) {
      setState({
        kind: 'bare_prefix',
        claim: accepted.barePrefix,
        reason: accepted.detail ?? accepted.message,
      });
      return;
    }
    if (!accepted.ok) {
      // The server rejected the upload itself (bad request, auth, network).
      // The load never started, so this is the whole outcome — show it here.
      setState({ kind: 'done', result: accepted });
      return;
    }

    // The load is now running server-side. Hand it to the global tracker,
    // which polls it and raises the completion notice on whatever page the
    // user is on — so we do NOT await it. Clear the file (state and the
    // underlying <input>, which can't be reset via state alone) and return.
    addLoadJob(accepted.data.job_id, { filename, graph, startedAt: Date.now() });
    setState({ kind: 'accepted', filename, graph });
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const busy = state.kind === 'uploading';
  const selectedSource = sources.find((s) => s.id === gitSourceId);
  const gitPathTrimmed = gitPath.trim();
  const gitPathError = validateGitPath(gitPathTrimmed);
  // A target graph is part of the contract — with none selected the old path
  // silently wrote to the ontology graph.
  const canLoadFromGit =
    Boolean(gitSourceId) && gitPathTrimmed !== '' && gitPathError === null && Boolean(graphBare);

  return (
    // One width for every tab, so switching never moves the heading or the
    // tab rows; the graph-upload form narrows itself inside it.
    <div className="container mx-auto max-w-6xl py-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold tracking-tight">Database Management</h1>
        <div className="flex items-center gap-2">
          <VdbBadge />
          {canManageSettings(me.data) && <VdbIndexStatusLine />}
        </div>
      </div>
      <p className="mt-1 text-sm text-tres-text-secondary" data-testid="current-database">
        Database:{' '}
        <span className="font-medium text-tres-status-active">
          {currentDatabase ? currentDatabase.name : '…'}
        </span>
        {currentDatabase && currentDatabase.name !== currentDatabase.id && (
          <span className="ml-1 font-mono text-xs">{currentDatabase.id}</span>
        )}
        {currentDatabase?.default && <span className="ml-1">(default)</span>}
        {databaseCount > 1
          ? ' — every load, clear and restore on this page lands here.'
          : " — the site's only database; every load, clear and restore lands here."}
      </p>
      <DatabaseSwitch />

      {/* The upload ban — visible on every tab while an import runs. */}
      <div className="mt-4">
        <LoadBanBanner />
      </div>

      <div className="mt-4 flex flex-wrap gap-x-2 border-b border-border">
        <TabButton active={pageTab === 'load'} disabled={busy} onClick={() => setPageTab('load')}>
          Load
        </TabButton>
        <TabButton
          active={pageTab === 'graphs'}
          disabled={busy}
          onClick={() => setPageTab('graphs')}
        >
          Graphs
        </TabButton>
        <TabButton
          active={pageTab === 'namespaces'}
          disabled={busy}
          onClick={() => setPageTab('namespaces')}
        >
          Namespaces
        </TabButton>
        <TabButton
          active={pageTab === 'troubleshoot'}
          disabled={busy}
          onClick={() => setPageTab('troubleshoot')}
        >
          Troubleshoot
        </TabButton>
        <TabButton active={pageTab === 'log'} disabled={busy} onClick={() => setPageTab('log')}>
          Transaction log
        </TabButton>
        {canBackup && (
          <TabButton active={pageTab === 'backup'} disabled={busy} onClick={() => setPageTab('backup')}>
            Backup
          </TabButton>
        )}
      </div>

      {pageTab === 'graphs' && (
        <>
          <GraphsSection graphs={scopeGraphs} />
          {/* Delete-by-source lives with the graph table: it manages STORED
              data, so it belongs where the stored data is looked at. Parking
              it at the bottom of the Load tab failed UAT twice-removed —
              "can't find delete by source anywhere" (2026-08-06). */}
          <SourcesSection />
        </>
      )}
      {pageTab === 'namespaces' && <NamespaceRegistryPanel />}
      {/* Troubleshoot manages the stored data's fit to its ontology, so it
          sits with the data (moved from the Query page, ruled 2026-10-13). */}
      {pageTab === 'troubleshoot' && <TroubleshootPanel />}
      {pageTab === 'log' && <TransactionLogSection graphs={scopeGraphs} />}
      {/* The whole-database backup is its own tab: at the foot of Load, Graph
          data nobody found it ("No snapshot option", UAT of 36.12.25,
          2026-10-18), the way delete-by-source was lost there before. */}
      {pageTab === 'backup' && canBackup && <SnapshotSection />}

      {pageTab === 'load' && (
        <>
      <div className="mt-6 flex flex-wrap gap-x-2 border-b border-border">
        <TabButton
          active={topTab === 'spreadsheet'}
          disabled={busy}
          onClick={() => setTopTab('spreadsheet')}
        >
          Spreadsheet
        </TabButton>
        {qros && (
          <TabButton
            active={topTab === 'folder'}
            disabled={busy}
            onClick={() => setTopTab('folder')}
          >
            Folder
          </TabButton>
        )}
        <TabButton active={topTab === 'graph'} disabled={busy} onClick={() => setTopTab('graph')}>
          Graph data
        </TabButton>
        {qros && (
          <TabButton
            active={topTab === 'extract'}
            disabled={busy}
            onClick={() => setTopTab('extract')}
          >
            Extract
          </TabButton>
        )}
        {!qros && (
          <TabButton
            active={topTab === 'gremlin'}
            disabled={busy}
            onClick={() => setTopTab('gremlin')}
          >
            Gremlin JSON
          </TabButton>
        )}
        {lpgEnabled && (
          <TabButton
            active={topTab === 'lpg'}
            disabled={busy}
            onClick={() => setTopTab('lpg')}
          >
            Property graph
          </TabButton>
        )}
      </div>

      {qros && topTab === 'extract' && (
        <div className="mt-6">
          <ExtractPanel uploadsLocked={uploadsLocked} />
        </div>
      )}

      {topTab === 'spreadsheet' && (
        <div className="mt-6">
          {qros ? (
            <SpreadsheetImportPanel
              key={mapTarget?.seq ?? 0}
              uploadsLocked={uploadsLocked}
              initialFile={mapTarget?.file ?? null}
              {...(mapTarget?.sheet ? { initialSheet: mapTarget.sheet } : {})}
              {...(mapTarget?.context ? { initialContext: mapTarget.context } : {})}
            />
          ) : (
            <RdfSpreadsheetImportPanel />
          )}
        </div>
      )}

      {/* Kept mounted while other tabs show so the staged session and plan
          survive a Map round-trip to the Spreadsheet wizard. */}
      {qros && (
        <div className={cn('mt-6', topTab !== 'folder' && 'hidden')}>
          <FolderImportPanel
            uploadsLocked={uploadsLocked}
            onMapSheet={(file, sheet, context) => {
              setMapTarget((prev) => ({
                file,
                ...(sheet ? { sheet } : {}),
                ...(context ? { context } : {}),
                seq: (prev?.seq ?? 0) + 1,
              }));
              setTopTab('spreadsheet');
            }}
          />
        </div>
      )}

      {topTab === 'gremlin' && !qros && (
        <div className="mt-6">
          <GremlinImportPanel />
        </div>
      )}

      {lpgEnabled && topTab === 'lpg' && (
        <div className="mt-6">
          <LpgImportPanel />
        </div>
      )}

      {topTab === 'graph' && (
        <div className="max-w-2xl">
      {/* The readable formats differ by engine, so this says so rather than
           advertising one list to both. QROS reads Turtle and N-Triples (a
           Turtle subset) plus its own native files; it cannot read RDF/XML,
           JSON-LD, N-Quads, or TriG, and until 2026-08-20 the copy offered
           three of those and the parser then blamed the file for a missing
           period. The graph sentence is gone for the same reason: a load can
           target a custom graph, and the picker below says so. The paragraph
           sits inside this tab's panel, so the tab row above never moves. */}
        <p className="mt-6 text-sm text-muted-foreground">
          {qros ? (
            <>
              Upload a Turtle (.ttl), N-Triples (.nt), or JSON-LD (.jsonld) file, or a QROS
              native file (.tqs, .tqd, .json). This engine cannot read RDF/XML, N-Quads, or
              TriG — convert those to Turtle first. A JSON-LD file needs its @context inline.
            </>
          ) : (
            <>Upload a Turtle, N-Triples, N-Quads, TriG, RDF/XML, or JSON-LD file.</>
          )}{' '}
          A load writes to exactly one graph: pick one below, or create your own. The four
          managed graphs — schema, vocabulary, catalog, and instance data — are a starting
          organization for your data, not a wall around it.
        </p>
      {gitEnabled && (
        <div className="mt-6 flex gap-2 border-b border-border">
          <TabButton
            active={sourceTab === 'direct'}
            disabled={busy}
            onClick={() => setSourceTab('direct')}
          >
            Direct upload
          </TabButton>
          <TabButton
            active={sourceTab === 'git'}
            disabled={busy}
            onClick={() => setSourceTab('git')}
          >
            Load from Git
          </TabButton>
        </div>
      )}

      <div className="mt-6 space-y-5">
        {sourceTab === 'direct' && (
          <label className="block">
            <span className="text-sm font-medium">{qros ? 'Graph or QROS file' : 'Graph data file'}</span>
            <input
              ref={fileInputRef}
              type="file"
              /* QROS lists only what it can actually read. The picker is a
                 hint, not a gate — a file dragged past it still meets a named
                 refusal from the engine rather than a parse error. */
              accept={
                qros
                  ? '.ttl,.nt,.jsonld,.json,.tqs,.tqd'
                  : '.ttl,.nt,.nq,.trig,.rdf,.xml,.jsonld,.json'
              }
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              disabled={busy || uploadsLocked}
              className={cn(
                'mt-1 block w-full text-sm',
                'file:mr-4 file:rounded-md file:border-0 file:bg-secondary',
                'file:px-4 file:py-2 file:text-sm file:font-medium file:text-secondary-foreground',
                'hover:file:bg-secondary/80',
                'disabled:opacity-50',
              )}
            />
            {file && (
              <span className="mt-1 block text-xs text-muted-foreground">
                {file.name} — {formatBytes(file.size)} · {rdfContentType(file.name)}
              </span>
            )}
            {qros && file && !busy && !uploadsLocked && !/\.(tqs|tqd)$/i.test(file.name) && (
              <p className="mt-2 rounded border border-tres-action/40 bg-tres-submenu-bg px-3 py-2 text-xs text-tres-text-primary">
                This file will be <strong>converted to the QROS native format</strong> on load:
                terms fold to the QROS vocabulary and classes are automatically anchored to the
                QROS base classes. The auto-classification is best-effort — review and correct it
                in the{' '}
                <Link to="/ontology" className="text-tres-action underline">
                  Ontology Editor
                </Link>{' '}
                afterward.
              </p>
            )}
          </label>
        )}

        {sourceTab === 'git' && (
          <fieldset disabled={busy} className="space-y-3">
            <legend className="text-sm font-medium">Git source</legend>
            <div className="block">
              <span className="text-xs text-muted-foreground">Repository</span>
              <select
                value={gitSourceId}
                onChange={(e) => setGitSourceId(e.target.value)}
                className={cn(
                  'mt-1 block w-full rounded-md border border-input bg-background px-3 py-2 text-sm',
                  'disabled:opacity-50',
                )}
              >
                <option value="">— pick a source —</option>
                {sources.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name} {s.authenticated ? '(authenticated)' : '(public)'}
                  </option>
                ))}
              </select>
              {selectedSource && (
                <span className="mt-1 block text-xs text-muted-foreground">
                  Base URL:{' '}
                  <span className="font-mono break-all">{selectedSource.base_url}</span>
                </span>
              )}
            </div>

            <label className="block">
              <span className="text-xs text-muted-foreground">File path</span>
              <input
                type="text"
                value={gitPath}
                onChange={(e) => setGitPath(e.target.value)}
                placeholder="ontology/core.ttl"
                spellCheck={false}
                className={cn(
                  'mt-1 block w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-sm',
                  'disabled:opacity-50',
                )}
              />
              {selectedSource && gitPathTrimmed && gitPathError === null && (
                <span className="mt-1 block text-xs text-muted-foreground break-all">
                  Final URL: <span className="font-mono">{selectedSource.base_url}{gitPathTrimmed}</span>
                </span>
              )}
              {gitPathError !== null && gitPathTrimmed && (
                <p className="mt-2 rounded border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
                  {gitPathError}
                </p>
              )}
            </label>
          </fieldset>
        )}

        <div className="flex items-center gap-3">
          {sourceTab === 'direct' ? (
            <button
              type="button"
              onClick={handleUpload}
              disabled={busy || uploadsLocked || !file || !graphBare?.trim()}
              className={cn(
                'rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground',
                'hover:bg-primary/90 disabled:opacity-50',
              )}
            >
              {busy ? 'Uploading…' : 'Upload'}
            </button>
          ) : (
            <button
              type="button"
              onClick={handleLoadFromGit}
              disabled={busy || uploadsLocked || !canLoadFromGit}
              className={cn(
                'rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground',
                'hover:bg-primary/90 disabled:opacity-50',
              )}
            >
              {busy ? 'Loading…' : 'Load from Git'}
            </button>
          )}
          {state.kind === 'uploading' && (
            <span className="text-xs text-muted-foreground">
              {sourceTab !== 'direct'
                ? 'Fetching from remote, then streaming to the database.'
                : 'Sending the file — the load runs in the background once it has arrived.'}
            </span>
          )}
        </div>
        {state.kind === 'bare_prefix' && (
          <Message kind="warning" density="compact" className="space-y-2">
            <p>{state.reason}</p>
            <div className="flex flex-wrap gap-2">
              {state.claim.convertible && (
                <Button variant="primary" size="sm" onClick={() => void performUpload(true)}>
                  Convert and load
                </Button>
              )}
              <Button
                variant="secondary"
                size="sm"
                onClick={() =>
                  setState({ kind: 'bare_prefix_cancelled', namespace: state.claim.namespace })
                }
              >
                Cancel
              </Button>
            </div>
          </Message>
        )}
        {state.kind === 'bare_prefix_cancelled' && (
          <Message kind="guidance" density="compact">
            Not loaded. Declare a prefix of your own for{' '}
            <span className="font-mono break-all">{state.namespace}</span> in the file (for example{' '}
            <span className="font-mono break-all">@prefix ex: &lt;{state.namespace}&gt; .</span>), write
            its terms with that prefix instead of :, and upload the file again.
          </Message>
        )}

        {/*
         * Target any graph: the managed starter set, an existing custom graph,
         * or "＋ New graph…" to create one by URI. A managed graph sends
         * `?kind=`, a custom graph sends `?graph=<uri>`. Single-select on
         * purpose — a load writes exactly one graph, so this is deliberately
         * NOT the page's multi-graph read scope.
         */}
        <fieldset disabled={busy} className="space-y-2">
          <legend className="text-sm font-medium">
            Target graph — this load writes here
            {currentDatabase && databaseCount > 1 ? `, in database ${currentDatabase.name}` : ''}
          </legend>
          <GraphSelector
            value={graphBare}
            onChange={setGraphBare}
            allowCreate
            storageKey="graphSelector.adminLoad"
            label=""
          />
          <p className="text-xs text-tres-text-secondary">
            {kindForBare(graphBare ?? '') ? (
              <>
                <span className="font-mono">{graphBare}</span>
                {' — '}
                {KIND_DESCRIPTIONS[kind]}
              </>
            ) : graphBare?.trim() ? (
              <>
                <span className="font-mono">{graphBare}</span>
                {' — custom named graph'}
              </>
            ) : (
              'Pick a graph, or create a new one by URI (e.g. urn:tres:graph:fms-process).'
            )}
          </p>
        </fieldset>

        <fieldset disabled={busy} className="space-y-2">
          <legend className="text-sm font-medium">Write mode</legend>
          <div className="flex flex-col gap-1">
            <label className="flex items-baseline gap-2 text-sm">
              <input
                type="radio"
                name="mode"
                value="append"
                checked={mode === 'append'}
                onChange={() => setMode('append')}
              />
              <span>
                <strong>Append</strong> — add to whatever's already in the graph. Safe for
                incremental loads.
              </span>
            </label>
            <label className="flex items-baseline gap-2 text-sm">
              <input
                type="radio"
                name="mode"
                value="replace"
                checked={mode === 'replace'}
                onChange={() => setMode('replace')}
              />
              <span>
                <strong>Replace</strong> — drop the target graph entirely, then load. Existing
                data in{' '}
                {targetGraphLabel ? (
                  <span className="font-mono">{targetGraphLabel}</span>
                ) : (
                  'the graph you pick above'
                )}{' '}
                is destroyed.
              </span>
            </label>
          </div>
          {mode === 'replace' && (
            <p className={messageClass('platform', 'compact', 'warning')}>
              Heads up: replace deletes everything currently in{' '}
              {targetGraphLabel ? (
                <span className="font-mono">{targetGraphLabel}</span>
              ) : (
                'the graph you pick above'
              )}{' '}
              before writing this file. Cannot be undone.
            </p>
          )}
        </fieldset>
      </div>

      {state.kind === 'accepted' && (
        <div className="mt-8 rounded-md border border-tres-action/40 bg-tres-action/10 p-4 text-sm text-tres-text-primary">
          Loading <span className="font-medium break-all">{state.filename}</span> into{' '}
          <span className="font-mono break-all">{state.graph}</span> in the background — you can
          leave this page; you&apos;ll be notified when it finishes.
        </div>
      )}

      {state.kind === 'done' && (
        <div className="mt-8">
          <ResultPanel result={state.result} />
        </div>
      )}

      {pendingReplace && targetGraphLabel && (
        <ConfirmGraphReplaceModal
          graph={targetGraphLabel}
          what={pendingReplace === 'git' ? gitPath.trim() : (file?.name ?? 'this file')}
          onCancel={() => setPendingReplace(null)}
          onConfirm={() =>
            pendingReplace === 'git'
              ? void performLoadFromGit()
              : void checkRulesThenUpload()
          }
        />
      )}

      {pendingRuleLoss && (
        <ConfirmRuleRetractionModal
          preflight={pendingRuleLoss}
          onCancel={() => setPendingRuleLoss(null)}
          onConfirm={() => void performUpload()}
        />
      )}

      {qros && <DownloadRulesSection />}
        </div>
      )}
        </>
      )}
    </div>
  );
}

/**
 * The index's state, read-only.
 *
 * There used to be a "Rebuild index" button here. It was removed 2026-08-26,
 * and the endpoint behind it is now an L3 SUPPORT function rather than a UI
 * affordance. Three reasons, all measured:
 *
 * - **The cost was unlabelled.** The endpoint restores when the store's
 *   fingerprint still matches and rebuilds when it does not, so one click was
 *   either ~30 seconds or over two hours, and nothing on screen said which.
 * - **A rebuild takes the product offline.** `set_ready(false)` holds for the
 *   whole build, so search, viz and AI grounding answer 503 throughout —
 *   MEASURED at 2,160 requests during one rebuild, none of which succeeded.
 * - **There is no way back.** The build runs on a detached thread until it
 *   completes or the process dies. No cancel, no progress, no undo.
 *
 * A load rebuilds the index automatically and a write merges into it
 * incrementally, so nothing routine needs this. Recovery is a documented
 * operator procedure with a stated window (CLOUD_OPERATOR_RUNBOOK section 3c),
 * which is where an hours-long irreversible operation belongs.
 */
function VdbIndexStatusLine() {
  const status = useVdbStatus();
  return (
    <div className="flex items-center gap-2">
      <VdbIndexStatus status={status.data} />
    </div>
  );
}

function VdbIndexStatus({ status }: { status: VdbStatus | undefined }) {
  const [open, setOpen] = useState(false);
  const { database } = useCurrentDatabase();
  if (!status) return null;
  if (status.state === 'failed') {
    return (
      <span role="alert" className="text-xs text-destructive">
        build failed — {status.error}
      </span>
    );
  }
  if (status.state === 'deferred') {
    return (
      <span role="alert" className="text-xs text-destructive">
        rebuild deferred — {deferredDetail(status)}
      </span>
    );
  }
  if (status.state !== 'ready') return null;
  const built = new Date(status.last_built);
  const when = Number.isNaN(built.getTime()) ? status.last_built : built.toLocaleTimeString();
  return (
    <span className="relative inline-flex">
      <button
        type="button"
        aria-label="Index details"
        title="Index details"
        onClick={() => setOpen((o) => !o)}
        onBlur={() => setOpen(false)}
        className={cn(
          'flex h-4 w-4 items-center justify-center rounded-full border text-[10px] leading-none',
          open
            ? 'border-tres-action text-tres-action'
            : 'border-input text-muted-foreground hover:border-tres-action hover:text-tres-action',
        )}
      >
        i
      </button>
      {open && (
        <div className="absolute right-0 top-6 z-10 w-max max-w-[calc(100vw-2rem)] rounded-md border border-border bg-card px-3 py-2 text-xs shadow-lg">
          {database && <p className="font-medium">Index of the {database.name} database</p>}
          <p>
            {status.entities.toLocaleString()} entities ·{' '}
            {status.triples.toLocaleString()} statements
          </p>
          <p className="text-muted-foreground">
            built {when} in {formatDuration(status.duration_ms / 1000)}
          </p>
          {status.warmed ? (
            <p className="text-muted-foreground">
              assistant ready {new Date(status.warmed.at).toLocaleTimeString()} · schema{' '}
              {status.warmed.schema_classes} classes, {status.warmed.schema_properties} properties (
              {status.warmed.summary_ms} ms) · profile ({status.warmed.profile_ms} ms) · resolver{' '}
              {status.warmed.resolver_terms === null ? 'not built' : `${status.warmed.resolver_terms} terms`} (
              {status.warmed.resolver_ms} ms)
            </p>
          ) : (
            <p className="text-muted-foreground">assistant indices not warmed yet</p>
          )}
          {status.waiting && (
            <p className="text-muted-foreground">
              Out of date: these are the index&apos;s counts until it rebuilds.
            </p>
          )}
        </div>
      )}
    </span>
  );
}

/**
 * Stop before a load that would DELETE stored rules. A named `@rules "set"`
 * block replaces its whole membership, so a partial file retracts every member
 * it omits. The rules are the deliverable — this is never a silent step.
 */
function ConfirmRuleRetractionModal({
  preflight,
  onCancel,
  onConfirm,
}: {
  preflight: RulePreflight;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg rounded-lg border border-destructive bg-card p-6 shadow-lg">
        <h2 className="text-lg font-semibold text-destructive">
          This load will delete {preflight.total_would_retract} stored rule
          {preflight.total_would_retract === 1 ? '' : 's'}
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          A named rule set replaces its entire membership on load. These rules are in the store but
          are not in the file you are loading, so they will be retracted:
        </p>
        <div className="mt-3 max-h-56 space-y-3 overflow-y-auto">
          {preflight.sets
            .filter((s) => s.would_retract.length > 0)
            .map((s) => (
              <div key={s.set}>
                <div className="font-mono text-xs text-muted-foreground">
                  {s.set} — file carries {s.incoming}
                </div>
                <ul className="mt-1 list-inside list-disc text-sm">
                  {s.would_retract.map((name) => (
                    <li key={name} className="font-mono">
                      {name}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          Cancel and download the rules first if you need to keep them.
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className={buttonVariants({ variant: 'secondary', size: 'md' })}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className={buttonVariants({ variant: 'dangerFilled', size: 'md' })}
          >
            Load and delete {preflight.total_would_retract} rule
            {preflight.total_would_retract === 1 ? '' : 's'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ===============================================================
// Graphs — create / clear / delete named graphs
// ===============================================================

function GraphsSection({ graphs: scopeGraphs }: { graphs: string[] }) {
  const auth = useAuth();
  const me = useMe();
  const qc = useQueryClient();
  const graphs = useGraphs();
  const [newName, setNewName] = useState('');
  const newUri = newGraphIri(newName);
  const [newRole, setNewRole] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<string | null>(null);
  const [clearJobs, setClearJobs] = useState<ClearJobView[]>([]);
  const [confirm, setConfirm] = useState<{ op: GraphOp; uri: string } | null>(null);
  const [download, setDownload] = useState<{ bare: string; label: string } | null>(null);

  const token = auth.user?.id_token;
  const invalidateAfterGraphOp = () => {
    invalidateAfterDataWrite(qc);
  };

  async function handleCreate() {
    if (!token || !newUri) return;
    setBusy('create');
    setError(null);
    setCreated(null);
    const r = await createGraph(newUri, token, newRole || undefined);
    setBusy(null);
    if (r.ok) {
      setCreated(`Created ${newName.trim()} (${newUri}).`);
      setNewName('');
      setNewRole('');
      invalidateAfterGraphOp();
    } else {
      setError(r.message);
    }
  }

  async function handleConfirmed(op: GraphOp, uri: string) {
    if (!token) return;
    setConfirm(null);
    setError(null);

    // Delete is a small op (a custom graph plus its role retraction) and stays
    // a plain request.
    if (op === 'delete') {
      setBusy(uri);
      const r = await deleteGraph(uri, token);
      setBusy(null);
      if (r.ok) invalidateAfterGraphOp();
      else setError(r.message);
      return;
    }

    // Clear and clear-all are JOBS (UAT 2026-09-02): the data graph can hold
    // millions of statements, and a held-open request timed out while the server
    // kept deleting. They are also QUEUED (#113): a clear asked for while another
    // runs is accepted and waits its turn, so several can be outstanding at once,
    // each with its own line.
    const toClear = op === 'clear-all' ? managedBare : [uri];
    const accepted =
      op === 'clear-all' ? await clearGraphs(toClear, token) : await clearGraph(uri, token);
    if (!accepted.ok) {
      setError(accepted.message);
      return;
    }
    const id = accepted.data.job_id;
    const show = (text: string, finished: boolean, failed: boolean) =>
      setClearJobs((jobs) => jobs.map((j) => (j.id === id ? { ...j, text, finished, failed } : j)));
    setClearJobs((jobs) => [
      ...jobs.filter((j) => !j.finished),
      { id, graphs: toClear, text: `Queued: ${toClear.join(', ')}`, finished: false, failed: false },
    ]);
    for (;;) {
      await new Promise((r) => setTimeout(r, 1500));
      const st = await graphClearJobStatus(id, token);
      if (!st.ok) {
        show(st.message, true, true);
        return;
      }
      show(clearJobLine(st.data), st.data.state !== 'running', st.data.state === 'failed');
      if (st.data.state === 'running') continue;
      if (st.data.state === 'done') invalidateAfterGraphOp();
      return;
    }
  }

  const managedBare = (graphs.data ?? []).filter((g) => g.managed).map((g) => g.bare);
  const outstanding = (bare: string) => clearJobs.some((j) => !j.finished && j.graphs.includes(bare));

  return (
    <div className="mt-6 space-y-4">
      <p className="text-sm text-muted-foreground">
        Named graphs partition the store. The four managed graphs are the starter set — they can
        be cleared but not deleted. Create custom graphs to hold independent ontologies or
        imported datasets; loading into a new graph from the Load tab creates it too.
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <input
          type="text"
          aria-label="Name of the new graph"
          value={newName}
          spellCheck={false}
          onChange={(e) => {
            setNewName(e.target.value);
            setCreated(null);
          }}
          placeholder="a name, such as FMS process"
          className={fieldClass('normal', 'w-80')}
        />
        <select
          value={newRole}
          onChange={(e) => setNewRole(e.target.value)}
          className="rounded-md border border-input bg-background px-2 py-1.5 text-sm"
          title="Role: the Dashboard's Classes, Object properties and Datatype properties cards count a graph with the role ontology. The other roles are recorded with the graph and change no page yet."
        >
          <option value="">Role: data (default)</option>
          <option value="ontology">Role: ontology</option>
          <option value="vocabulary">Role: vocabulary</option>
          <option value="catalog">Role: catalog</option>
        </select>
        <button
          type="button"
          onClick={() => void handleCreate()}
          disabled={busy === 'create' || !newUri}
          className={buttonVariants({ variant: 'primary', size: 'md' })}
        >
          {busy === 'create' ? 'Creating…' : 'Create graph'}
        </button>
      </div>
      {newUri && !created && (
        <p className="text-xs text-muted-foreground">
          Its address will be <span className="font-mono">{newUri}</span>.
        </p>
      )}
      {created && <p className="text-sm text-muted-foreground">{created}</p>}
      {error && <div className="text-sm text-destructive">{error}</div>}
      {clearJobs.map((j) => (
        <div
          key={j.id}
          className={cn('text-sm', j.failed ? 'text-destructive' : 'text-muted-foreground')}
        >
          {j.text}
        </div>
      ))}

      {/* The operator's "clear and re-stamp": one request, one rebuild. Clearing
          the four graphs one at a time rebuilt the index over data the next
          clear deleted, and that was enough to kill a task (2026-08-27). */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setConfirm({ op: 'clear-all', uri: CLEAR_ALL_PHRASE })}
          disabled={managedBare.length === 0 || managedBare.every(outstanding)}
          className={buttonVariants({ variant: 'secondary', size: 'md' })}
          title="Empty the managed graphs together — one write, one index rebuild"
        >
          Clear all managed graphs
        </button>
      </div>

      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-border text-left text-muted-foreground">
            <th className="py-1.5 pr-3 font-medium">Actions</th>
            <th className="py-1.5 pr-3 font-medium">Graph</th>
            <th className="py-1.5 pr-3 font-medium">Type</th>
            {/* The count is what makes Clear legible: without it a user has no
                way to tell whether clearing did anything. It reads 0 after. */}
            <th className="py-1.5 pr-3 text-right font-medium">Statements</th>
          </tr>
        </thead>
        <tbody>
          {inScope(graphs.data ?? [], scopeGraphs, (g) => g.bare).map((g) => (
            <tr key={g.bare} className="border-b border-border/50">
              <td className="py-1.5 pr-3">
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => setConfirm({ op: 'clear', uri: g.bare })}
                    disabled={busy === g.bare || outstanding(g.bare)}
                    className="rounded p-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
                    title="Empty this graph — the graph itself (and its role) remains. Clears queue: ask for several and they run in turn."
                  >
                    {outstanding(g.bare) ? '…' : 'Clear'}
                  </button>
                  {canExportStore(me.data) && (
                    <button
                      type="button"
                      onClick={() => setDownload({ bare: g.bare, label: graphDisplayLabel(g) })}
                      className="rounded p-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
                      title="Export this graph as a file (Admin and Owner)"
                    >
                      Download
                    </button>
                  )}
                  {!g.managed && (
                    <button
                      type="button"
                      onClick={() => setConfirm({ op: 'delete', uri: g.bare })}
                      disabled={busy === g.bare || outstanding(g.bare)}
                      className={buttonVariants({ variant: 'ghost', size: 'icon' })}
                      title="Delete graph and its role declaration"
                    >
                      Delete
                    </button>
                  )}
                </div>
              </td>
              <td className="py-1.5 pr-3 font-mono text-foreground">{g.bare}</td>
              <td className="py-1.5 pr-3 text-muted-foreground">
                {g.managed
                  ? `managed · ${graphDisplayLabel(g)}`
                  : g.kind
                    ? `custom · role: ${g.kind}`
                    : 'custom'}
              </td>
              <td
                className={cn(
                  'py-1.5 pr-3 text-right font-mono tabular-nums',
                  g.statements === 0 ? 'text-muted-foreground' : 'text-foreground',
                )}
              >
                {g.statements === undefined ? '—' : g.statements.toLocaleString()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {graphs.isLoading && <div className="text-sm text-muted-foreground">Loading…</div>}

      {download && (
        <DownloadGraphModal
          graphBare={download.bare}
          label={download.label}
          onClose={() => setDownload(null)}
        />
      )}
      {confirm && (
        <ConfirmGraphOpModal
          op={confirm.op}
          graph={(graphs.data ?? []).find((g) => g.bare === confirm.uri) ?? null}
          managed={(graphs.data ?? []).filter((g) => g.managed)}
          onCancel={() => setConfirm(null)}
          onConfirm={() => void handleConfirmed(confirm.op, confirm.uri)}
        />
      )}
    </div>
  );
}

/** One outstanding or just-finished clear on the Graphs tab (#113). */
interface ClearJobView {
  id: string;
  graphs: string[];
  text: string;
  finished: boolean;
  failed: boolean;
}

// ===============================================================
// Sources — distinct @source attestations, with wholesale removal
// ===============================================================

function SourcesSection() {
  const auth = useAuth();
  const qc = useQueryClient();
  const me = useMe();
  // Collapsed by default -- dozens of external URLs otherwise bury the rest of
  // the tab, and the list is reference material, not the task.
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<SourceCount | null>(null);
  const [lastResult, setLastResult] = useState<string | null>(null);

  const token = auth.user?.id_token;
  const qros = isQros(me.data);

  // Cached like the graphs list above (UAT 2026-09-01): the list stays put
  // across visits and refreshes when an action invalidates it — every data
  // write goes through invalidateAfterDataWrite, which sweeps this key too.
  const sourcesQuery = useQuery<SourceCount[], Error>({
    queryKey: ['data-sources'],
    enabled: qros && !!token,
    queryFn: async () => {
      const r = await listSources(token!);
      if (!r.ok) throw new Error(r.message);
      return r.data.sources;
    },
  });
  const sources = sourcesQuery.data ?? null;
  const listError = sourcesQuery.isError ? String(sourcesQuery.error.message) : null;

  if (!qros) return null;

  const handleDelete = async (s: SourceCount) => {
    if (!token) return;
    setConfirming(null);
    setBusy(s.source);
    setLastResult(null);
    // A job, not a held-open request: accept at once, then poll. The old
    // synchronous form timed out in the browser while the server kept
    // deleting for an hour (45.7.3).
    const accepted = await deleteSource(s.source, token);
    if (!accepted.ok) {
      setBusy(null);
      setError(accepted.message);
      return;
    }
    for (;;) {
      await new Promise((r) => setTimeout(r, 1500));
      const st = await sourceDeleteJobStatus(accepted.data.job_id, token);
      if (!st.ok) {
        setBusy(null);
        setError(st.message);
        return;
      }
      const j = st.data;
      if (j.state === 'running') {
        const gone = j.progress.deleted + j.progress.stripped;
        setLastResult(
          gone > 0
            ? `Removing “${s.source}”… ${gone.toLocaleString()} statement(s) processed.`
            : `Removing “${s.source}”…`,
        );
        continue;
      }
      setBusy(null);
      if (j.state === 'done' && j.result) {
        setLastResult(
          `Removed “${s.source}”: ${j.result.statements_deleted.toLocaleString()} statement(s) deleted, ` +
            `${j.result.attestations_stripped.toLocaleString()} co-attested statement(s) kept without it.`,
        );
        invalidateAfterDataWrite(qc);
      } else {
        setLastResult(null);
        setError(describeApiError(j.http_status ?? 500, j.error ?? 'The source was not removed', j.detail));
      }
      return;
    }
  };

  return (
    <div className="mt-10 space-y-3">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={cn(buttonVariants({ variant: 'ghost', size: 'icon' }), 'flex w-full items-center gap-1.5 uppercase tracking-wide')}
      >
        <span
          className={cn('inline-block transition-transform', open ? 'rotate-90' : 'rotate-0')}
          aria-hidden
        >
          ▶
        </span>
        Sources
        {sources !== null && sources.length > 0 && (
          <span className="rounded bg-tres-action/15 px-1.5 py-0 text-[10px] normal-case text-tres-action">
            {sources.length}
          </span>
        )}
      </button>
      {!open ? null : (
      <>
      <p className="max-w-2xl text-sm text-muted-foreground">
        Statements tagged <span className="font-mono">@source</span> name the feed or file that
        attested them. Deleting a source removes every statement only it attests; statements
        other sources also attest just lose its entry. Derived facts recompute automatically.
      </p>

      {(error ?? listError) && (
        <div className="rounded border border-destructive bg-destructive/10 p-2 text-xs text-destructive">
          {error ?? listError}
        </div>
      )}
      {lastResult && <div className="text-xs text-muted-foreground">{lastResult}</div>}

      {sources === null ? (
        <div className="text-sm text-muted-foreground">Loading sources…</div>
      ) : sources.length === 0 ? (
        <div className="text-sm text-muted-foreground">
          No sourced statements — data loaded so far carries no{' '}
          <span className="font-mono">@source</span> tags.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border border-border bg-card">
          <table className="w-full text-left text-sm">
            <thead className="bg-muted/50 text-xs text-muted-foreground">
              <tr>
                <th className="px-3 py-2 font-medium">Actions</th>
                <th className="px-3 py-2 font-medium">Source</th>
                <th className="px-3 py-2 font-medium">Statements</th>
                <th className="px-3 py-2 font-medium">Delete removes</th>
                <th className="px-3 py-2 font-medium">Kept (co-attested)</th>
                <th className="px-3 py-2 font-medium">Last loaded</th>
                <th className="px-3 py-2 font-medium">Uploaded by</th>
              </tr>
            </thead>
            <tbody>
              {sources.map((s) => (
                <tr key={s.source} className="border-t border-border align-top">
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      onClick={() => setConfirming(s)}
                      disabled={busy !== null}
                      className={buttonVariants({ variant: 'danger', size: 'sm' })}
                    >
                      {busy === s.source ? 'Deleting…' : 'Delete'}
                    </button>
                  </td>
                  <td className="px-3 py-2 font-mono font-medium">{s.source}</td>
                  <td className="px-3 py-2 tabular-nums">{s.statements.toLocaleString()}</td>
                  <td className="px-3 py-2 tabular-nums">{s.sole_attested.toLocaleString()}</td>
                  <td className="px-3 py-2 tabular-nums">
                    {(s.statements - s.sole_attested).toLocaleString()}
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    {s.last_imported_at ? new Date(s.last_imported_at).toLocaleString() : '—'}
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    {s.last_loaded_by ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      </>
      )}

      {confirming && (
        <ConfirmSourceDeleteModal
          source={confirming}
          onCancel={() => setConfirming(null)}
          onConfirm={() => void handleDelete(confirming)}
        />
      )}
    </div>
  );
}

function ConfirmSourceDeleteModal({
  source,
  onCancel,
  onConfirm,
}: {
  source: SourceCount;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const [typed, setTyped] = useState('');
  const match = typed.trim() === source.source;
  const coAttested = source.statements - source.sole_attested;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="w-full max-w-lg rounded-lg border border-border bg-background p-5 shadow-lg">
        <h2 className="text-lg font-semibold">Delete source</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          {source.sole_attested} statement{source.sole_attested === 1 ? '' : 's'} attested only
          by <span className="font-mono">{source.source}</span> will be removed
          {coAttested > 0 && (
            <>
              ; {coAttested} co-attested statement{coAttested === 1 ? '' : 's'} will remain and
              lose its entry
            </>
          )}
          . Derived facts recompute afterwards. This cannot be undone.
        </p>
        <label className="mt-4 block">
          <span className="text-xs text-muted-foreground">Type the source name to confirm:</span>
          <input
            type="text"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            spellCheck={false}
            autoFocus
            placeholder={source.source}
            className="mt-1 block w-full rounded-md border border-input bg-background px-2 py-1.5 font-mono text-sm"
          />
        </label>
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className={buttonVariants({ variant: 'secondary', size: 'lg' })}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!match}
            className={buttonVariants({ variant: 'dangerFilled', size: 'lg' })}
          >
            Delete source
          </button>
        </div>
      </div>
    </div>
  );
}

// ===============================================================
// Transaction log — data operations (loads, imports, graph ops)
// ===============================================================

const isRuleEntry = (e: AuditEntry) => e.action.startsWith('rules.');
const graphOfEntry = (e: AuditEntry) => (isRuleEntry(e) ? null : (e.target ?? null));

function TransactionLogSection({ graphs }: { graphs: string[] }) {
  const auth = useAuth();
  const token = auth.user?.id_token;
  const audit = useAuditLog(token, true, 'data');
  // `target` is the graph an operation addressed, except on a rule's row,
  // where it is the rule; entries that name no graph stay visible under any
  // scope.
  const entries = inScope(audit.data?.entries ?? [], graphs, graphOfEntry);

  return (
    <div className="mt-6 space-y-4">
      <p className="text-sm text-muted-foreground">
        Every change to the data on this instance — file loads, spreadsheet and folder imports,
        graph create, clear and delete, and every edit made on screen to entities, the ontology,
        the vocabulary and the rules — with who made it, the graph it touched, and what changed.
      </p>
      <div className="overflow-hidden rounded-md border border-border bg-card">
        {audit.isLoading && (
          <p className="px-3 py-3 text-sm text-muted-foreground">Loading…</p>
        )}
        {audit.error && (
          <p className="px-3 py-3 text-sm text-destructive">
            Could not load the transaction log: {audit.error.message}
          </p>
        )}
        {audit.data && !audit.data.available && (
          <p className="px-3 py-3 text-sm text-muted-foreground">
            Transaction-log storage isn’t configured on this instance (no state directory).
          </p>
        )}
        {audit.data?.available && audit.data.entries.length === 0 && (
          <p className="px-3 py-3 text-sm text-muted-foreground">
            No data operations recorded yet. Operations performed before this version shipped
            are not in the log.
          </p>
        )}
        {audit.data?.available && audit.data.entries.length > 0 && entries.length === 0 && (
          <p className="px-3 py-3 text-sm text-muted-foreground">
            No operations against the selected graphs. Widen the graph selection to see the
            rest of the log.
          </p>
        )}
        {audit.data && entries.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-3 py-2 font-semibold">When</th>
                  <th className="px-3 py-2 font-semibold">Who</th>
                  <th className="px-3 py-2 font-semibold">Operation</th>
                  <th className="px-3 py-2 font-semibold">Graph</th>
                  <th className="px-3 py-2 font-semibold">Detail</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.id} className="border-b border-border/50 last:border-b-0 align-top">
                    <td className="whitespace-nowrap px-3 py-1.5 text-muted-foreground">{e.ts}</td>
                    <td className="px-3 py-1.5">{e.actor_email ?? e.actor_name ?? e.actor}</td>
                    <td className="whitespace-nowrap px-3 py-1.5 font-mono text-xs">{e.action}</td>
                    <td className="px-3 py-1.5 font-mono text-xs">{graphOfEntry(e) ?? '—'}</td>
                    <td className="px-3 py-1.5 text-muted-foreground">
                      {isRuleEntry(e) && e.target ? `Rule ${e.target}; ` : ''}
                      {e.detail ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/** Per-graph export, as a dialog off the Graphs table's Actions column.
 *  Was a separate "Download graph" section listing every graph again; folding
 *  it into the row keeps one place per graph for everything you can do to it. */
function DownloadGraphModal({
  graphBare,
  label,
  onClose,
}: {
  graphBare: string;
  label: string;
  onClose: () => void;
}) {
  const auth = useAuth();
  const me = useMe();
  // QROS emits native (.tqd data / .tqs schema) or Turtle; the RDF line
  // formats are not faithfully derivable from the native store, so they're
  // hidden there (and the server returns 406 if asked). The ontology graph
  // exports as the native schema lens (.tqs) or as Turtle — the exporter
  // converts native predicates back to rdf/rdfs/owl, so the .ttl loads into
  // any RDF tool. RDF backends offer the full RDF set.
  const formats: Array<[ExportFormat, string]> = isQros(me.data)
    ? kindForBare(graphBare) === 'ontology'
      ? [
          ['tqs', 'QROS Schema (.tqs)'],
          ['turtle', 'Turtle'],
        ]
      : [
          ['turtle', 'Turtle'],
          ['tqd', 'QROS (.tqd)'],
        ]
    : [
        ['turtle', 'Turtle'],
        ['ntriples', 'N-Triples'],
        ['nquads', 'N-Quads'],
        ['rdfxml', 'RDF/XML'],
        ['jsonld', 'JSON-LD'],
      ];
  const [busy, setBusy] = useState<ExportFormat | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function doDownload(format: ExportFormat) {
    const token = auth.user?.id_token;
    if (!token) return;
    setBusy(format);
    setError(null);
    const result = await downloadGraph(graphBare, format, token);
    if (!result.ok) {
      setError(result.message);
    }
    setBusy(null);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="w-full max-w-lg rounded-lg border border-border bg-background p-5 shadow-lg">
        <h2 className="text-lg font-semibold">Download graph</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Export <span className="font-medium text-foreground">{label}</span>{' '}
          (<span className="font-mono">{graphBare}</span>) as a single file — for a backup,
          to share, or to move it to another TReS instance. Pick a format:
        </p>
        <div className="mt-4 flex flex-wrap gap-1">
          {formats.map(([fmt, fmtLabel]) => (
            <button
              key={fmt}
              type="button"
              onClick={() => void doDownload(fmt)}
              disabled={busy !== null}
              className={cn(
                'rounded border border-border bg-background px-2 py-1 text-xs font-medium text-foreground',
                'hover:bg-muted disabled:opacity-50',
              )}
            >
              {busy === fmt ? '…' : fmtLabel}
            </button>
          ))}
        </div>
        {error && <p className="mt-3 text-xs text-destructive">{error}</p>}
        <div className="mt-5 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className={buttonVariants({ variant: 'secondary', size: 'md' })}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Rules download. A graph export carries statements only — rules live in their
 * own store, so without this an app-produced backup silently loses the entire
 * rule layer. Emits native `.tqs` that re-imports through the ordinary Load
 * path. QROS-only; the RDF backend has no rules.
 */
function DownloadRulesSection() {
  const auth = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function doDownload() {
    const token = auth.user?.id_token;
    if (!token) return;
    setBusy(true);
    setError(null);
    const result = await downloadRules(null, token);
    if (!result.ok) {
      setError(result.message);
    }
    setBusy(false);
  }

  return (
    <div className="mt-10 border-t border-border pt-6">
      <h2 className="text-lg font-semibold tracking-tight">Download rules</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Export every stored rule as a single native file. Rules are not part of a graph export, so
        a complete backup needs this file as well as the graph files.
      </p>
      <div className="mt-4 rounded-md border border-border bg-card px-4 py-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1">
            <div className="text-sm font-medium">All rules</div>
            <div className="text-xs text-muted-foreground">
              Named rule sets and rules created here, in one .tqs
            </div>
          </div>
          <button
            type="button"
            onClick={() => void doDownload()}
            disabled={busy}
            className={cn(
              'rounded border border-border bg-background px-2 py-1 text-xs font-medium text-foreground',
              'hover:bg-muted disabled:opacity-50',
            )}
          >
            {busy ? '…' : 'QROS Rules (.tqs)'}
          </button>
        </div>
        {error && <p className="mt-2 text-xs text-destructive">{error}</p>}
      </div>
    </div>
  );
}

/**
 * What the load did to the RULE STORE. Retractions are the destructive half of
 * the named-set contract and are shown by name, not counted.
 */
function RuleLoadPanel({ rules }: { rules: LoadResponse['rules'] }) {
  const nothing =
    rules.loaded.length === 0 &&
    rules.retracted.length === 0 &&
    rules.failed.length === 0 &&
    !rules.materialization_error;
  if (nothing) return null;

  return (
    <div className="mt-3 space-y-2 border-t border-tres-success/60 pt-3 text-xs">
      <div className="font-medium">Rules</div>
      {rules.loaded.length > 0 && (
        <div>
          Loaded {rules.loaded.length}:{' '}
          <span className="font-mono">{rules.loaded.join(', ')}</span>
        </div>
      )}
      {rules.retracted.length > 0 && (
        <div className="rounded border border-destructive bg-destructive/10 p-2 text-destructive">
          <div className="font-medium">
            Retracted {rules.retracted.length} rule
            {rules.retracted.length === 1 ? '' : 's'} by set replacement
          </div>
          <ul className="mt-1 list-inside list-disc font-mono">
            {rules.retracted.map((r) => (
              <li key={`${r.set}/${r.name}`}>
                {r.name} <span className="opacity-70">({r.set})</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {rules.failed.length > 0 && (
        <div className="rounded border border-destructive bg-destructive/10 p-2 text-destructive">
          <div className="font-medium">
            {rules.failed.length} rule{rules.failed.length === 1 ? '' : 's'} failed to load
          </div>
          <ul className="mt-1 list-inside list-disc">
            {rules.failed.map((f) => (
              <li key={f.name}>
                <span className="font-mono">{f.name}</span>: {f.error}
              </li>
            ))}
          </ul>
        </div>
      )}
      {rules.materialization_error ? (
        <div className="rounded border border-destructive bg-destructive/10 p-2 text-destructive">
          <div className="font-medium">Rule materialization failed</div>
          <div className="mt-1">{rules.materialization_error}</div>
          <div className="mt-1">
            No facts are being derived until this is fixed. Check the Rules page.
          </div>
        </div>
      ) : (
        rules.derived > 0 && <div>Derived {rules.derived.toLocaleString()} fact(s).</div>
      )}
    </div>
  );
}

function ResultPanel({ result }: { result: ApiResult<LoadResponse> }) {
  if (!result.ok) {
    return (
      <Message kind="warning" className="whitespace-pre-wrap p-4">
        {result.message}
      </Message>
    );
  }

  const d = result.data;
  return (
    <div className={messageClass('outcome')}>
      <div className="font-medium">Loaded successfully</div>
      <dl className="mt-2 grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 font-mono text-xs">
        <dt className="text-muted-foreground">bytes</dt>
        <dd>{d.bytes.toLocaleString()}</dd>
        <dt className="text-muted-foreground">graph</dt>
        <dd>{d.graph}</dd>
        <dt className="text-muted-foreground">duration_ms</dt>
        <dd>{d.duration_ms.toLocaleString()}</dd>
      </dl>
      {d.fuseki_response && (
        <div className="mt-2 text-xs text-muted-foreground">
          Load result: {d.fuseki_response}
        </div>
      )}
      <RuleLoadPanel rules={d.rules} />
    </div>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

/** Client-side mirror of `validate_relative_path` in
 *  `crates/tres-api/src/handlers/admin.rs`. Returns `null` when the
 *  trimmed path is acceptable, or a short message describing what's
 *  wrong. Server enforces this again — the client check just gives
 *  immediate feedback. */
function validateGitPath(path: string): string | null {
  if (path === '') return null; // empty = "not entered yet", different message lives on the button
  if (path.length > 500) return 'Path must be 500 characters or fewer.';
  if (path.startsWith('/')) return 'Path must be relative — drop the leading slash.';
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(path))
    return 'Path must be a relative path, not an absolute URL.';
  if (path.split('/').some((seg) => seg === '..'))
    return 'Path must not contain `..` segments.';
  // The control-character class is the point of this check — a path carrying a
  // NUL or an escape is exactly what must be refused — so the rule that guards
  // against writing one by accident is suppressed deliberately here.
  // eslint-disable-next-line no-control-regex
  if (/[\s\x00-\x1f]/.test(path))
    return 'Path contains whitespace or control characters.';
  return null;
}

function TabButton({
  active,
  disabled,
  onClick,
  children,
}: {
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        '-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors',
        active
          ? 'border-primary text-foreground'
          : 'border-transparent text-muted-foreground hover:text-foreground',
        'disabled:opacity-50',
      )}
    >
      {children}
    </button>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="container mx-auto flex min-h-[40vh] max-w-md items-center justify-center text-center text-sm">
      <div>{children}</div>
    </div>
  );
}


/**
 * The whole database as one file, and the way back (36.12.6): every graph in
 * native form with its prefixes, the rules and the corrections, so a set
 * moves between sites and databases without a graph-by-graph export.
 */
function SnapshotSection() {
  const auth = useAuth();
  const me = useMe();
  const { addLoadJob } = useLoadJobs();
  const databases = useDatabases();
  const { active } = useActiveDatabase();
  const [busy, setBusy] = useState<'restore' | null>(null);
  const [taking, setTaking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const canDownload = canExportStore(me.data);
  // A restore writes the whole database, so it asks what the download asks.
  const canRestore = canExportStore(me.data);
  const current = active ?? databases.data?.default ?? 'main';
  const currentName = databases.data?.databases.find((d) => d.id === current)?.name ?? current;
  const token = auth.user?.id_token;
  const contents = useQuery({
    queryKey: ['snapshot-contents', current],
    queryFn: () => fetchSnapshotContents(token ?? ''),
    enabled: !!token && canDownload,
  });
  if (!canDownload && !canRestore) return null;
  const holds = contents.data?.ok ? snapshotHoldsLine(contents.data.data) : null;

  async function doRestore() {
    const token = auth.user?.id_token;
    if (!token || !file) return;
    setBusy('restore');
    setError(null);
    setOutcome(null);
    const r = await restoreSnapshot(file, token);
    setBusy(null);
    if (r.ok) {
      addLoadJob(r.data.job_id, {
        filename: file.name,
        graph: `database ${currentName}`,
        startedAt: Date.now(),
      });
      setOutcome(
        `Restoring ${file.name} into ${currentName}; the load job shows its progress, and the index rebuilds when it finishes.`,
      );
      setFile(null);
    } else {
      setError(r.message);
    }
  }

  return (
    <div className="mt-10 border-t border-border pt-6">
      <h2 className="text-lg font-semibold tracking-tight">Snapshot</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        The whole of database <span className="font-medium">{currentName}</span> as one zip:
        every graph in native form with its prefixes, the rules with their on-or-off state, the
        corrections, and the site's own tables: import mappings, question banks with their runs
        and grades, asked questions, saved queries, dashboard cards and layouts. A graph download
        is one graph; this is the backup. Restore it into the database in use on this site or
        another; the restore adds to what that database holds and overwrites nothing.
      </p>
      {holds && (
        <p className="mt-2 text-sm" data-testid="snapshot-holds">
          This snapshot holds {holds}.
        </p>
      )}
      <div className="mt-4 rounded-md border border-border bg-card px-4 py-3">
        {canDownload && (
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex-1">
              <div className="text-sm font-medium">Download a snapshot</div>
              <div className="text-xs text-muted-foreground">
                One .zip of {currentName}, for a move, a copy or a backup
              </div>
            </div>
            <button
              type="button"
              onClick={() => setTaking(true)}
              disabled={busy !== null}
              className={buttonVariants({ variant: 'secondary', size: 'sm' })}
            >
              Take a snapshot…
            </button>
          </div>
        )}
        {canRestore && (
          <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-border pt-3">
            <div className="flex-1">
              <div className="text-sm font-medium">Restore a snapshot into {currentName}</div>
              <div className="text-xs text-muted-foreground">
                Pick a .zip a TReS site downloaded; its graphs, prefixes, rules, corrections and
                site tables are added to {currentName}
              </div>
            </div>
            <input
              type="file"
              accept=".zip,application/zip"
              aria-label="Snapshot file"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              disabled={busy !== null}
              className="text-xs"
            />
            <button
              type="button"
              onClick={() => void doRestore()}
              disabled={busy !== null || !file}
              className={buttonVariants({ variant: 'primary', size: 'sm' })}
            >
              {busy === 'restore' ? 'Starting…' : 'Restore'}
            </button>
          </div>
        )}
        {taking && (
          <SnapshotModal databaseId={current} databaseName={currentName} onClose={() => setTaking(false)} />
        )}
        {error && <div className={cn('mt-3', messageClass('warning', 'compact'))}>{error}</div>}
        {outcome && <div className={cn('mt-3', messageClass('outcome', 'compact'))}>{outcome}</div>}
      </div>
    </div>
  );
}
